import {
  useState,
} from 'react';
import {
  Callout,
  DEFAULT_BASE_CURRENCY,
  Field,
  PasswordInput,
  PASSWORD_MIN_LENGTH,
  Select,
  baseCurrencyOptions,
  EmailInput,
  TextInput,
  useLoad,
  countryOptions,
} from '@affdice/portal-kit';
import {
  PlatformApi,
} from '../../client.ts';
import { t } from '../../messages.ts';
import { Failure } from '../shared.tsx';

/**
 * Onboarding an operator — Ch. 50.
 *
 * §50.1a is why this form says what it does at the end rather than at the top:
 * somebody who has just filled in six fields is about to tell a colleague the
 * tenant is ready, and the moment to correct that is after they press the
 * button, not before they start.
 */
export function AddOperator({ api, onDone }: { api: PlatformApi; onDone: () => void }) {
  const [legal, setLegal] = useState('');
  const [display, setDisplay] = useState('');
  const [currency, setCurrency] = useState<string>(DEFAULT_BASE_CURRENCY);
  const [country, setCountry] = useState('');
  const currencies = useLoad(() => api.currencies(), []);
  const currencyOptions =
    currencies.state === 'ready' && currencies.value.items.length > 0
      ? currencies.value.items.map((c) => ({ value: c.code, label: `${c.code} — ${c.name}` }))
      : baseCurrencyOptions();
  const [ownerName, setOwnerName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [temp, setTemp] = useState('');
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);

  return (
    <div className="form">
      {failure ? <Failure error={failure} /> : null}

      <div className="form-row">
        <Field label={t('ops.add.legalName')} hint={t('ops.add.legalHint')}>
          <TextInput value={legal} onChange={setLegal} placeholder="Kodedice Pvt Ltd" />
        </Field>
        <Field label={t('ops.add.tradingName')} hint={t('ops.add.tradingHint')}>
          <TextInput value={display} onChange={setDisplay} placeholder="Kodedice" />
        </Field>
      </div>

      {/* Country first: it is the answer the currency follows from — an
          operator's books are kept in the currency of where it is registered —
          and asking for the currency before the country asks somebody to
          answer the consequence before the cause. No timezone field: the
          owner's call (2026-10-12) is that every operator is stored in UTC and
          times are converted to the viewer's own zone only on screen, so the
          server's UTC default stands. */}
      <div className="form-row">
        <Field label={t('common.country')}>
          <Select value={country} onChange={setCountry} options={[
            { value: '', label: t('ops.add.choose') },
            ...countryOptions(),
          ]} />
        </Field>
        <Field label={t('ops.add.baseCurrency')}>
          <Select value={currency} onChange={setCurrency} options={currencyOptions} />
        </Field>
      </div>

      <div className="form-row">
        <Field label={t('ops.add.ownerName')} hint={t('ops.add.ownerNameHint')}>
          <TextInput value={ownerName} onChange={setOwnerName} placeholder="Vaibhav Pansambal" />
        </Field>
        <Field label={t('ops.add.ownerPhone')} hint={t('ops.add.optional')}>
          <TextInput value={phone} onChange={setPhone} placeholder="+44 20 7946 0958" />
        </Field>
      </div>

      <div className="form-row">
        <Field label={t('ops.add.ownerEmail')} hint={t('ops.add.ownerEmailHint')}>
          <EmailInput value={email} onChange={setEmail} placeholder="owner@vela.example" />
        </Field>
        <Field label={t('ops.add.tempPassword')} hint={t('ops.add.tempHint')}>
          <PasswordInput
            value={temp}
            onChange={setTemp}
            autoComplete="new-password"
            minLength={PASSWORD_MIN_LENGTH}
          />
        </Field>
      </div>

      <div className="form-actions">
        <button
          className="primary"
          disabled={
            busy || !legal || !display || !email || temp.length < PASSWORD_MIN_LENGTH ||
            country.trim().length !== 2
          }
          onClick={() => {
            setBusy(true);
            setFailure(null);
            setCreated(null);
            api
              .createOperator({
                legal_name: legal, display_name: display, base_currency: currency,
                country: country.trim(),
                owner_email: email,
                owner_temporary_password: temp,
                ...(ownerName.trim() ? { owner_name: ownerName.trim() } : {}),
                ...(phone.trim() ? { owner_phone: phone.trim() } : {}),
              })
              .then((r) => { setCreated(r.next_steps); onDone(); })
              .catch((e: unknown) => setFailure(e))
              .finally(() => setBusy(false));
          }}
        >
          {busy ? t('ops.add.creating') : t('ops.add.create')}
        </button>
      </div>

      {/* §50.1a — after the button, because the person who just filled in six
          fields is about to tell somebody the tenant is ready. Batch 2
          review, finding 7 — the server's own next-steps text; a result,
          read whole. */}
      {created ? <Callout fold={false}>{created}</Callout> : null}
    </div>
  );
}
